import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { usePurchaseStatusPolling } from '@hub/hooks/use-purchase-status-polling';
import { useRecentAccountCheck } from '@hub/hooks/use-recent-account-check';
import { useRepayOrder } from '@hub/hooks/use-repay-order';
import { trackMetaPixelPurchase } from '@hub/utils/meta-pixel-utils';
import { Alert, Box, useTheme } from '@mui/material';
import { clearAllCheckoutTracking } from '@monorepo/client-common';
import { DesignIcon } from '@monorepo/nova-icons';
import {
  Button,
  Dialog,
  LinearProgress,
  Typography,
  useBreakpoints,
} from '@monorepo/nova-ui';
import { PurchaseStatus, SupportedCurrencies } from '@monorepo/types';
import {
  getRedirectMessage,
  getStatusDescription,
  getStatusIcon,
  getStatusTitle,
} from './after-purchase-utils';

const AfterPurchaseModal = () => {
  const { snapshotId } = useParams();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [progress, setProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { isMobile } = useBreakpoints();
  // Check for recently created accounts and redirect if found

  const { repay, isLoading: isRepaying } = useRepayOrder((error) => {
    setErrorMessage(error.message || 'Failed to check payment status');
  });
  // Use polling hook if we have a purchaseId, otherwise fall back to timer
  const {
    data: statusData,
    status,
    isPolling: _isPolling,
    timeRemaining: _timeRemaining,
    totalProgress,
    purchaseId,
  } = usePurchaseStatusPolling({
    snapshotId: snapshotId || '',
    enabled: !!snapshotId,
    timeoutMs: 1 * 25 * 1000, // 25 seconds timeout
    onError: (error) => {
      setErrorMessage(error.message || 'Failed to check payment status');
    },
  });

  useRecentAccountCheck(purchaseId, status);

  // Track purchase with Meta Pixel when status becomes Completed
  // Also clear checkout tracking session data
  useEffect(() => {
    if (status === PurchaseStatus.Completed && purchaseId && statusData) {
      trackMetaPixelPurchase(
        statusData.amount ?? 0,
        statusData.currency as SupportedCurrencies,
        purchaseId,
      );
      clearAllCheckoutTracking();
    }
  }, [status, purchaseId, statusData]);

  // Progress calculation for polling mode
  useEffect(() => {
    if (!snapshotId) return;
    setProgress(totalProgress);
  }, [totalProgress, snapshotId]);

  return (
    <Dialog
      open
      variant="bare"
      fullScreen={isMobile}
      disableEscapeKeyDown
      aria-labelledby="after-purchase-title"
      aria-describedby="after-purchase-description"
      slotProps={{
        paper: {
          sx: {
            bgcolor: 'background.contrast',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            maxWidth: '534px',
            width: '100%',
            maxHeight: isMobile ? '100%' : '472px',
            height: '100%',
          },
        },
      }}
    >
      <Box
        sx={{
          gap: status === PurchaseStatus.Failed ? 2 : 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: status === PurchaseStatus.Failed ? 1.5 : 2,
          }}
        >
          <DesignIcon
            iconName={getStatusIcon(status, isDarkMode)}
            useMuiSvgIcon={false}
          />
        </Box>
        {/* Conditionally render progress bar - hide for failed state */}
        {status !== PurchaseStatus.Failed && (
          <Box
            sx={{
              width: '70%',
              maxWidth: 400,
              mb: status === PurchaseStatus.Pending ? 2.5 : 2,
            }}
          >
            <LinearProgress variant="determinate" value={progress} />
          </Box>
        )}

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2, maxWidth: 400 }}>
            {errorMessage}
          </Alert>
        )}

        <Typography
          id="after-purchase-title"
          variant="h6"
          component="h1"
          fontWeight="400"
          fontStyle={'italic'}
        >
          {getStatusTitle(status)}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: status === PurchaseStatus.Failed ? 1.5 : 2.5,
            textAlign: 'center',
          }}
        >
          <Typography
            id="after-purchase-description"
            variant="body1"
            sx={{
              textAlign: 'center',
              maxWidth: 400,
            }}
          >
            {getStatusDescription(snapshotId, status)}
          </Typography>
          {getRedirectMessage(snapshotId, status) && (
            <Typography
              variant="caption"
              sx={{
                textAlign: 'center',
                maxWidth: [350, 400],
              }}
            >
              {getRedirectMessage(snapshotId, status)}
            </Typography>
          )}

          {/* Show retry button for failed state */}
          {status === PurchaseStatus.Failed && (
            <Button
              variant="primary"
              loading={isRepaying}
              onClick={() => repay(snapshotId || '')}
              sx={{
                mt: 2,
                px: 4,
                py: 1.5,
              }}
            >
              Retry Payment
            </Button>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default AfterPurchaseModal;
